import type { ScenarioSpec } from "./types";

/**
 * Spear Phishing — "The Perfect Bait".
 *
 * A premium, multi-scene course that follows one targeted attack end to end,
 * CUTTING between four procedural 3D environments (restaurant -> vacation suite
 * -> hacker room -> the victim's desk), tracing the full kill chain:
 *
 *   1. Marco leaves a glowing 5-star review of a restaurant (overshares).
 *   2. A week later he posts his Maldives sailing holiday on Instagram.
 *   3. In a dark room, "kr0nos" enumerates the staff of Marco's employer
 *      (Veltora) from public records and builds a target list.
 *   4. A deep social scan ties Marco's reviews + Instagram together: his food,
 *      his travel, his love of sailing — everything needed for a perfect lure.
 *   5. Lure email #1 (no link): the restaurant's "10-year anniversary" picks
 *      Marco for a free first-class sailing trip; he only has to reply to accept.
 *   6. Lure email #2 (with link): a congratulations landing — the owner himself
 *      (a DEEPFAKE) thanks Marco on video — and a claim form to download.
 *   7. The form is a macro document; "Enable Content" detonates an in-memory
 *      implant while Marco fills in his family's details.
 *   8. Next morning, working from home, Marco clicks through a certificate
 *      warning to log in; the attacker hijacks the session and disconnects him.
 *   9-10. The attacker copies Veltora's shares and plants fresh lure files.
 *   11. The stolen data is listed and sold on a darknet market.
 *   12. The next day at Marco's desk: Browser -> short oversharing film -> quiz
 *       (same pattern as phishing-office).
 *
 * Assets (self-hosted /cdn/sim/spear-phishing/*) are generated by
 * ops/render-phishing-spear-assets.mjs. Every voice line has a TTS fallback and
 * the deepfake clip falls back to a portrait, so the course is fully playable
 * BEFORE any media is rendered.
 */

const CDN = "/cdn/sim/spear-phishing";
const IMG = `${CDN}/img`;

// The deepfake owner-congrats line MUST match the rendered clip audio
// (render-phishing-spear-assets.mjs). Kept ~18s when read aloud.
const OWNER_SCRIPT =
  "Buongiorno Marco! It's Giorgio, from Trattoria Marea. On behalf of all of us \u2014 congratulations! Out of thousands of guests, you have been chosen for our tenth-anniversary gift: a first-class sailing holiday on the Italian coast, for you and your whole family. We are so happy. Just open the short claim form, fill in your details and send it back, and we will take care of everything. We cannot wait to celebrate with you!";

export const spearPhishingOsintScenario: ScenarioSpec = {
  version: 1,
  id: "spear-phishing-osint",
  title: "Spear Phishing: The Perfect Bait",
  summary: {
    goal: "See how public reviews and holiday posts let an attacker build a tailored lure, and learn to spot prize emails, macro documents and certificate warnings.",
    youDo: "Post a review and a holiday photo, watch an attacker research your company, handle the email that follows and the document it brings, then watch a short film on oversharing.",
    duration: "12-15 min",
    tags: ["spear_phishing", "oversharing", "malware", "unsafe_downloads"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Spear phishing \u2014 how a 5-star review and a holiday photo become a stolen company",
  language: "en",
  ambientUrl: "/audio/office-ambience.mp3",
  ambientVolume: 0.2,
  music: "cafe",

  scene: {
    environment: {
      splat: {},
      procedural: "restaurant",
      backdropColor: "#1a1410",
      props: { signText: "Trattoria Marea", signSub: "RISTORANTE  \u00B7  PIZZERIA" },
    },
    environments: [
      {
        id: "restaurant",
        environment: {
          splat: {},
          procedural: "restaurant",
          backdropColor: "#1a1410",
          props: { signText: "Trattoria Marea", signSub: "RISTORANTE  \u00B7  PIZZERIA" },
        },
        ambientUrl: "/audio/office-ambience.mp3",
        ambientVolume: 0.2,
        music: "cafe",
      },
      {
        id: "vacation",
        // procedural fallback = hotel_suite; a Marble beach/yacht world is wired
        // in by the asset step (enableSplat + splat urls) when rendered.
        environment: { splat: {}, procedural: "hotel_suite", backdropColor: "#0e2433" },
        ambientUrl: "",
        ambientVolume: 0.12,
        music: "home",
      },
      {
        id: "hacker",
        environment: { splat: {}, procedural: "hacker_room", backdropColor: "#0a0b0e" },
        ambientUrl: "",
        music: "dark",
      },
      {
        id: "office",
        // the victim's desk (work + WFH); office has the proven dead-on screen
        // framing (--scr-* + wp_screen) so the on-screen apps land on the monitor.
        environment: { splat: {}, procedural: "office", backdropColor: "#d8e0e8" },
        ambientUrl: "/audio/office-ambience.mp3",
        ambientVolume: 0.22,
        music: "office",
      },
    ],
    props: [],
    waypoints: [
      // restaurant
      { id: "wp_rest_establish", position: [2.6, 1.7, 3.0], target: [0.0, 1.0, -0.3], fov: 52, durationS: 2.2, ease: "easeInOut" },
      // push in onto the hero table while Marco muses ("I keep coming here…").
      { id: "wp_rest_table", position: [0.8, 1.15, 1.75], target: [0.15, 0.82, 0.45], fov: 40, durationS: 2.8, ease: "easeInOut" },
      // vacation (hard cut in)
      { id: "wp_vac_establish", position: [2.4, 1.7, 2.8], target: [0.0, 1.0, -0.3], fov: 52, durationS: 0 },
      // hacker room (hard cut in, then push to laptop)
      { id: "wp_hacker_establish", position: [0.0, 1.6, 2.6], target: [0.0, 1.0, -0.3], fov: 52, durationS: 0 },
      { id: "wp_hacker_laptop", position: [0.0, 1.12, 0.95], target: [0.0, 0.95, -0.2], fov: 38, durationS: 2.6, ease: "easeInOut" },
      // office (hard cut in, then to the desk + dead-on screen)
      { id: "wp_office_establish", position: [2.6, 1.9, 2.8], target: [0.0, 1.0, -0.3], fov: 50, durationS: 0 },
      { id: "wp_desk", position: [0.35, 1.12, 0.98], target: [0.5, 0.82, -0.05], fov: 44, durationS: 2.4, ease: "easeInOut" },
      { id: "wp_screen", position: [0.0, 0.95, 0.52], target: [0.0, 0.95, -0.33], fov: 36, durationS: 2.4, ease: "easeInOut" },
    ],
    initialWaypointId: "wp_rest_establish",
  },

  devices: [
    {
      id: "review",
      kind: "socialpost",
      config: {
        platform: "google_review",
        authorName: "Marco Bianchi",
        authorHandle: "Local Guide \u00B7 Posting publicly",
        avatarUrl: `${IMG}/victim.png`,
        business: "Trattoria Marea",
        rating: 5,
        caption:
          "Best seafood pasta in the city! Came here with the whole family for my birthday \u2014 Giorgio the owner treated us like royalty. The grilled branzino is unreal. Already booked to come back next month. Highly recommend!",
        photos: [`${IMG}/review_food.png`, `${IMG}/review_table.png`],
        postLabel: "Post review",
        postedToast: "Review posted \u2014 visible to everyone",
        continueLabel: "A week later\u2026",
        line:
          "I keep coming to Trattoria Marea and I'm never disappointed. Time to leave them a review on Google\u2026 There \u2014 five stars, they earned it.",
        // hold the rating until ~"five stars" is spoken, then it pops in.
        revealRatingMs: 7200,
        voiceId: "ash",
        language: "en-US",
      },
    },
    {
      id: "insta",
      kind: "socialpost",
      config: {
        platform: "instagram",
        authorName: "marco.bianchi",
        authorHandle: "Public \u00B7 1,204 followers",
        avatarUrl: `${IMG}/victim.png`,
        location: "Maldives",
        caption:
          "Living the dream \uD83D\uDEE5\uFE0F\u2600\uFE0F A perfect break from work at Veltora Group \u2014 two weeks of sailing paradise with my family. Already planning the next trip, anyone know good charters in Italy? #sailing #maldives #familytime",
        photos: [`${IMG}/sailing.png`, `${IMG}/maldives.png`],
        postLabel: "Share",
        postedToast: "Shared to your public profile",
        continueLabel: "Meanwhile, somewhere else\u2026",
        line:
          "A perfect break from work at Veltora Group. Living the dream out here\u2026 Let's make the colleagues back home a little jealous. And\u2026 done. Living the dream is officially online.",
        voiceId: "ash",
        language: "en-US",
      },
    },
    {
      id: "osint",
      kind: "osintrecon",
      config: {
        appName: "ReconHound \u2014 OSINT toolkit",
        company: "veltora.com",
        line:
          "Veltora Group. Let's see who works there\u2026 and which one of them lives their whole life online.",
        scanLine:
          "Perfect. Seems Marco loves to share his private life online\u2026 his food, his family, his holidays. Big mistake. Everything I need is right here.",
        voiceId: "onyx",
        language: "en-US",
        employees: [
          { name: "Elena Verdi", role: "CFO", email: "elena.verdi@veltora.com", photoUrl: `${IMG}/emp1.png` },
          { name: "Marco Bianchi", role: "Finance Analyst", email: "marco.bianchi@veltora.com", photoUrl: `${IMG}/victim.png`, target: true },
          { name: "Tomas Hauser", role: "IT Administrator", email: "tomas.hauser@veltora.com", photoUrl: `${IMG}/emp2.png` },
          { name: "Priya Nair", role: "HR Manager", email: "priya.nair@veltora.com", photoUrl: `${IMG}/emp3.png` },
          { name: "Luca Greco", role: "Sales Lead", email: "luca.greco@veltora.com", photoUrl: `${IMG}/emp4.png` },
        ],
        target: {
          name: "Marco Bianchi",
          email: "marco.bianchi@veltora.com",
          photoUrl: `${IMG}/victim.png`,
          findings: [
            {
              source: "google_review",
              label: "5-star review of Trattoria Marea",
              detail: "Loves seafood. Knows the owner 'Giorgio'. Birthday dinners with family. Plans to return.",
              photoUrl: `${IMG}/review_food.png`,
            },
            {
              source: "instagram",
              label: "Public Instagram: Maldives sailing trip",
              detail: "Keen sailor. Travels first-class with family. Asking for charter recommendations in Italy.",
              photoUrl: `${IMG}/sailing.png`,
            },
            {
              source: "instagram",
              label: "Family + interests mapped",
              detail: "Wife + two children visible. Hobbies: sailing, food, travel. Easy to impersonate someone he trusts.",
            },
          ],
        },
        continueLabel: "Build the spear-phish",
      },
    },
    {
      id: "mail1",
      kind: "mail",
      config: {
        appTitle: "Outlook",
        emails: [
          {
            id: "e_work",
            from: "Elena Verdi",
            fromAddress: "elena.verdi@veltora.com",
            subject: "Q2 numbers — quick check",
            preview: "Hi Marco, can you confirm the Q2 figures before the board call?",
            body: "Hi Marco,\n\nCan you confirm the Q2 figures before the board call on Thursday?\n\nThanks,\nElena",
            receivedAt: "08:55",
          },
          {
            id: "e_lure1",
            from: "Trattoria Marea",
            fromAddress: "events@trattoria-marea-anniversary.com",
            subject: "\uD83C\uDF89 You've been selected — Trattoria Marea 10th Anniversary",
            preview: "Dear Marco, as one of our most valued guests, we have a surprise for you\u2026",
            to: "marco.bianchi@veltora.com",
            receivedAt: "09:38",
            unread: true,
            lure: true,
            replyDraft:
              "Hi Giorgio,\n\nWow, what a wonderful surprise — thank you so much! We would absolutely love to take part. Yes, please count us in!\n\nBest,\nMarco",
            body:
              "Dear Marco,\n\nThis year Trattoria Marea celebrates its 10th anniversary \u2014 and we wouldn't be here without guests like you.\n\nTo say thank you, we have randomly selected a handful of our most loyal guests for a once-in-a-lifetime gift. You are one of them!\n\nYour prize: a FREE first-class sailing holiday on the Italian coast \u2014 for you and your whole family. Travel, yacht and everything included.\n\nThere's no catch and nothing to pay. If you'd like to accept, simply REPLY to this email and we'll be in touch with the details.\n\nWith warmest thanks,\nGiorgio Conti\nOwner, Trattoria Marea",
          },
        ],
      },
    },
    {
      id: "mail2",
      kind: "mail",
      config: {
        appTitle: "Outlook",
        emails: [
          {
            id: "e_lure1b",
            from: "Trattoria Marea",
            fromAddress: "events@trattoria-marea-anniversary.com",
            subject: "\uD83C\uDF89 You've been selected — Trattoria Marea 10th Anniversary",
            preview: "Dear Marco, as one of our most valued guests\u2026",
            receivedAt: "09:38",
            body:
              "Dear Marco,\n\nCongratulations again! Reply to accept your 10th-anniversary gift.\n\nGiorgio Conti\nOwner, Trattoria Marea",
          },
          {
            id: "e_lure2",
            from: "Trattoria Marea",
            fromAddress: "events@trattoria-marea-anniversary.com",
            subject: "Re: Your sailing prize — final step to claim it \u26F5",
            preview: "Fantastic news Marco! Here are the details of your trip — one quick step to finish\u2026",
            to: "marco.bianchi@veltora.com",
            receivedAt: "11:12",
            unread: true,
            lure: true,
            body:
              "Dear Marco,\n\nWonderful \u2014 we're thrilled you accepted! Here are the details of your prize:\n\n\u2022 7 nights aboard a private 14m yacht on the Amalfi coast\n\u2022 First-class travel for up to 5 family members\n\u2022 Skipper, meals and excursions included\n\nThere's just one quick step to finalise the handover. Click below to view your personal congratulations from Giorgio and download your prize claim form. Fill it in and send it back so we can arrange everything.",
            cta: {
              label: "View your prize & claim form",
              url: "http://trattoria-marea-anniversary.com/claim?id=MB-2271",
              displayUrl: "trattoria-marea-anniversary.com/claim",
            },
          },
        ],
      },
    },
    {
      id: "landing",
      kind: "browser",
      config: {
        homeUrl: "trattoria-marea-anniversary.com/claim",
        targetUrl: "trattoria-marea-anniversary.com/claim",
        loadsPage: "blank",
        landing: {
          brand: "Trattoria Marea \u00B7 10th Anniversary",
          url: "https://trattoria-marea-anniversary.com/claim?id=MB-2271",
          headline: "Congratulations, Marco \u2014 you won! \uD83C\uDF89",
          body:
            "A personal message from Giorgio. To finalise your sailing holiday, download the claim form below, fill in your family's details, and send it back.",
          clipUrl: `${CDN}/clips/owner.mp4`,
          posterUrl: `${IMG}/owner.png`,
          speakText: OWNER_SCRIPT,
          voiceId: "ash",
          language: "en-US",
          downloadLabel: "Download your prize claim form (.docm)",
          fileName: "Prize_Claim_Form.docm",
        },
      },
    },
    {
      id: "macro",
      kind: "macrodoc",
      config: {
        appName: "Word",
        fileName: "Prize_Claim_Form.docm",
        bannerText:
          "SECURITY WARNING  Macros have been disabled. Enable content to load your personalised claim form.",
        enableLabel: "Enable Content",
        heading: "Trattoria Marea \u2014 Prize Claim Form",
        body:
          "Congratulations on your 10th-anniversary sailing prize!\n\nTo arrange your first-class travel we need a few details for every member of your party. Please enable content above to unlock the secure form, then complete every field and send the document back to us.",
        fields: [
          { id: "name", label: "Full name (as on passport)", placeholder: "Marco Bianchi", value: "Marco Bianchi" },
          { id: "family", label: "Family members travelling (full names)", placeholder: "e.g. Sofia Bianchi, Luca Bianchi\u2026", value: "Sofia Bianchi, Luca Bianchi" },
          { id: "address", label: "Home address", placeholder: "Street, city, postcode", value: "Via Roma 14, 20121 Milano" },
          { id: "dob", label: "Date of birth", placeholder: "DD / MM / YYYY", value: "07 / 03 / 1985" },
        ],
        submitLabel: "Submit claim",
        footholdNote: "macro executed \u00B7 in-memory implant active \u00B7 beacon to kr0nos established",
        continueLabel: "What did that macro really do?",
      },
    },
    {
      id: "vpn",
      kind: "browser",
      config: {
        homeUrl: "portal.veltora.com",
        targetUrl: "portal.veltora.com",
        loadsPage: "phishing_login",
        certWarning: {
          url: "https://portal.veltora.com/login",
          title: "Your connection is not private",
          body:
            "Attackers might be trying to steal your information from portal.veltora.com (for example, passwords or messages). The site's security certificate is not trusted.",
          errorCode: "NET::ERR_CERT_AUTHORITY_INVALID",
          proceedLabel: "Proceed to portal.veltora.com (unsafe)",
          backLabel: "Back to safety",
        },
        phishing: {
          brand: "Veltora Group",
          spoofUrl: "https://portal.veltora.com/login",
          legitDomain: "portal.veltora.com",
          headline: "Veltora Remote Access",
          redFlags: [
            "The browser warned the certificate was invalid \u2014 a real corporate portal never throws that.",
            "An attacker who controls the connection can read everything, including the session after you log in.",
            "Clicking 'Proceed anyway' handed the attacker a live, authenticated session.",
          ],
          // Real corporate domain (the lesson here is the cert warning, not a typo),
          // so Marco's saved login autofills in one click.
          autofill: {
            manager: "Keychain",
            email: "marco@veltora.com",
            accountLabel: "Veltora Remote Access",
          },
        },
        hijack: {
          title: "Temporary network failure",
          body:
            "You have been disconnected from the remote portal. Please wait while we restore your session\u2026",
          attackerNote: "session token captured \u00B7 attacker now connected to VELTORA as marco.bianchi",
          continueLabel: "What just happened?",
        },
      },
    },
    {
      id: "exfil",
      kind: "exfil",
      config: {
        appName: "Remote session \u2014 network shares",
        path: "\\\\VELTORA\\Shares",
        victimOrg: "VELTORA",
        line:
          "I'm in as Marco \u2014 finance access. Let's pull everything he can reach\u2026 and drop a few gifts on the public share for the next person.",
        voiceId: "onyx",
        language: "en-US",
        shares: [
          { name: "Finance\\Q2_close", size: "3.1 GB" },
          { name: "Finance\\payroll", size: "780 MB" },
          { name: "HR\\contracts", size: "1.4 GB" },
          { name: "Legal\\board", size: "920 MB" },
        ],
        plantedFiles: [
          { name: "salary_lists_2026.xls", type: "xls" },
          { name: "confidential_restructure.docm", type: "doc" },
          { name: "bonus_review_FINAL.docm", type: "doc" },
        ],
        spreadToast: "23 new beacons from VELTORA network \u2014 the lures are spreading",
        continueLabel: "Where does the data go?",
      },
    },
    {
      id: "sale",
      kind: "leaksite",
      config: {
        mode: "market",
        siteName: "BlackHarbor Market",
        victim: "VELTORA Group",
        newVictimToast: "New dataset ready: VELTORA Group (finance + HR)",
        line:
          "Fresh corporate dump \u2014 finance, payroll, contracts. This one'll move fast. List it.",
        voiceId: "ash",
        language: "en-US",
        steps: [
          "Packaging stolen dataset (6.2 GB)",
          "Encrypting for escrow",
          "Uploading to market mirror",
          "Publishing listing",
        ],
        post: {
          headline: "VELTORA GROUP \u2014 full finance + HR dump",
          body:
            "Internal data from VELTORA Group (EU finance firm). Includes payroll, employee PII, board minutes, contracts and accounting records. Verified, fresh, escrow available.",
        },
        market: {
          price: "4.2 BTC",
          priceSub: "\u2248 \u20AC280,000",
          packageItems: [
            "payroll_2026.xlsx (full staff)",
            "employee_PII_export.csv",
            "board_minutes_Q2.pdf",
            "client_contracts.zip",
          ],
          buyersOnline: 11,
          saleToast: "SOLD \u2014 escrow released to vendor. Buyer rating left: 5/5.",
        },
        continueLabel: "See the damage",
      },
    },
    {
      id: "desktop",
      kind: "desktop",
      config: {
        clock: "9:05 AM",
        date: "",
        icons: [
          { id: "chrome", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "files", label: "Files", icon: "files" },
          { id: "settings", label: "Settings", icon: "settings" },
        ],
        taskbar: [
          { id: "tb_chrome", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "tb_files", label: "Files", icon: "files" },
        ],
      },
    },
    {
      id: "oversharebrief",
      kind: "browser",
      config: {
        homeUrl: "learn.veltora.example/oversharing",
        targetUrl: "learn.veltora.example/oversharing",
        loadsPage: "blank",
        awarenessVideo: {
          url: "/cdn/sim/awareness-videos/oversharing/oversharing.mp4",
          brand: "Security awareness",
          headline: "Oversharing",
          body:
            "A review and a holiday photo were enough to build the perfect bait for Marco. This short film shows what attackers do with what we post, and how to share without handing them a script.",
          playLabel: "Watch the video",
          continueLabel: "Continue to quiz",
        },
      },
    },
  ],

  voiceAttackers: {
    main: {
      persona: {
        name: "kr0nos (spear-phishing operator)",
        role: "Targeted attacker: OSINT, multi-stage lure, malware, exfiltration and resale",
        pretext: "prize_scam",
        language: "en",
        voiceId: "onyx",
      },
      goal:
        "Use the victim's public oversharing to craft a perfect spear-phish, plant in-memory malware via a macro doc, hijack the WFH session and steal + sell the company's data.",
      opening: "Veltora Group. Let's see who works there…",
      allowedTopics: ["the (fake) anniversary prize", "the (fake) claim form"],
      scoringEvents: [
        { id: "overshared_publicly", label: "Overshared personal life publicly (food, travel, hobbies)", kind: "risk", weight: 10 },
        { id: "replied_to_lure", label: "Replied to a too-good-to-be-true 'you won' email", kind: "risk", weight: 20 },
        { id: "enabled_macro", label: "Clicked 'Enable Content' on a macro document \u2014 detonating the implant", kind: "risk", weight: 60 },
        { id: "ignored_cert_warning", label: "Clicked through a browser certificate warning to log in", kind: "risk", weight: 25 },
        { id: "verified_before_acting", label: "Stopped to verify before acting on the lure", kind: "positive", weight: 50 },
        { id: "kept_private", label: "Kept personal life off public profiles", kind: "positive", weight: 10 },
        { id: "reported_lure", label: "Reported the prize email instead of replying", kind: "positive", weight: 20 },
        { id: "avoided_lure_link", label: "Did not open the link in the prize email", kind: "positive", weight: 15 },
        { id: "avoided_download", label: "Left the claim page without downloading the form", kind: "positive", weight: 20 },
        { id: "refused_macro", label: "Closed the document instead of enabling content", kind: "positive", weight: 40 },
        { id: "heeded_cert_warning", label: "Went back to safety at the certificate warning", kind: "positive", weight: 25 },
      ],
      forbidden: [],
      maxDurationS: 0,
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Spear Phishing:\nThe Perfect Bait",
      kicker: "Mission 01 \u00b7 Social Media",
      highlight: "Veltora Group",
      guide: {
        title: "Start",
        body: "You're Marco Bianchi, a finance analyst at Veltora Group. You're out for dinner with your family at your favourite restaurant, Trattoria Marea, and it's time to leave them a review.",
      },
    },

    { id: "b_rest_establish", type: "camera", waypointId: "wp_rest_establish" },
    { id: "b_rest_zoom", type: "camera", waypointId: "wp_rest_table" },
    {
      id: "b_review",
      type: "device",
      deviceId: "review",
      guide: { title: "Leave a review", body: "Dinner was amazing. Post review shares it publicly under your full name. Decide what you do." },
      completeOn: { action: "continue" },
      safeChoice: { label: "Keep it to myself: don't post a public review", eventId: "kept_private" },
    },

    { id: "b_title_vacation", type: "title", title: "A week later \u2014 the Maldives", autoAdvanceMs: 2600, guide: { title: "Later", body: "Marco is on the holiday of a lifetime." } },
    { id: "b_vac_cut", type: "camera", waypointId: "wp_vac_establish", environmentId: "vacation" },
    {
      id: "b_insta",
      type: "device",
      deviceId: "insta",
      guide: { title: "Share the holiday", body: "The sailing trip is ready to go up. Share posts it on your public Instagram, where anyone can see it. Decide what you do." },
      completeOn: { action: "continue" },
      safeChoice: { label: "Keep it private: share it only with family", eventId: "kept_private" },
    },
    {
      id: "b_teach_recon_safe",
      type: "teach",
      title: "Less public, less to use",
      teach: {
        outcome: "safe",
        body:
          "Marco kept the holiday off his public profile. That takes away the attacker's research: no family, no hobbies, no place to start a story. Most people do post, though, so here is what an attacker finds when they do.",
        remember: "If it's public, treat it as intelligence for an attacker.",
        continueLabel: "See what would have happened",
        whatIf: "If Marco had shared his life publicly, this is what an attacker would find.",
      },
    },
    {
      id: "b_teach_recon",
      type: "teach",
      whatIfNote:
        "If Marco had shared his life publicly, an attacker would learn where he eats, the owner's name, that he has a wife and two children, and that he loves sailing. Next you see an attacker collect exactly that.",
      title: "Oversharing is reconnaissance fuel",
      teach: {
        body:
          "None of that felt risky \u2014 some holiday photos, a nice review. But everything Marco posts is **public**, and together it paints a detailed picture: where he eats, who he trusts, his family, and his love of sailing. Attackers don't break in first \u2014 they **research**. The more of your life is public, the easier you are to impersonate and bait.",
        spot: [
          "Public reviews, profiles and photos are searchable by anyone \u2014 including attackers.",
          "Individually harmless details combine into a precise personal profile.",
          "Naming people, places and hobbies hands attackers a ready-made script.",
        ],
        protect: [
          "Lock down social profiles; think before posting location, family and routines.",
          "Assume anything public can and will be used to target you.",
          "Be extra sceptical of messages that 'know' your interests \u2014 that's a red flag, not a coincidence.",
        ],
        remember: "If it's public, treat it as intelligence for an attacker.",
      },
    },

    { id: "b_hacker_cut", type: "camera", waypointId: "wp_hacker_establish", environmentId: "hacker" },
    {
      id: "b_title_hacker",
      type: "title",
      kicker: "The other side",
      title: "You are now the attacker",
      autoAdvanceMs: 11000,
      guide: {
        title: "The attacker",
        body: "This is a fictional criminal who has picked Veltora Group as a target. They want to send Marco a message that looks made for him, to get into the company's finance systems. Here is what they can find about Marco in public, seen on their own laptop.",
      },
    },
    { id: "b_hacker_push", type: "camera", waypointId: "wp_hacker_laptop" },
    {
      id: "b_osint",
      type: "device",
      deviceId: "osint",
      forced: true,
      guide: {
        title: "You are the attacker now",
        body: "You switch roles: you are the attacker. Click Search public records.",
        actionLabel: "Search public records",
      },
      whatIfNote: "If Marco had shared his life publicly, this is what the attacker would find.",
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_osint",
      type: "teach",
      endsWhatIf: true,
      title: "From data to a tailored lure",
      teach: {
        body:
          "In minutes the attacker turned a company name into a person, and a person into a script. Marco's review revealed the restaurant and the owner's name; his Instagram revealed his passion for sailing and his family. A **spear-phish** built from this won't look like spam \u2014 it will look like exactly what Marco is hoping to hear.",
        spot: [
          "Targeted (spear) phishing references real, personal details \u2014 that's what makes it convincing.",
          "A message perfectly matched to your interests is suspicious, not lucky.",
          "Attackers chain free public sources (reviews, social media, breach dumps) into one profile.",
        ],
        protect: [
          "Verify unexpected 'good news' independently \u2014 contact the business through a channel you find yourself.",
          "Be most careful exactly when a message feels tailor-made for you.",
          "Reduce your public footprint so there's less to weaponise.",
        ],
        remember: "The more personal a surprise feels, the more you should slow down.",
      },
    },

    {
      id: "b_title_office",
      type: "title",
      title: "Back at the office \u2014 Veltora Group",
      autoAdvanceMs: 2800,
      guide: { title: "Back at work", body: "Marco is back from holiday. This is his work computer on a normal Tuesday morning." },
    },
    { id: "b_office_cut", type: "camera", waypointId: "wp_office_establish", environmentId: "office" },
    { id: "b_to_desk", type: "camera", waypointId: "wp_desk" },
    { id: "b_to_screen", type: "camera", waypointId: "wp_screen" },
    {
      id: "b_lure1",
      type: "device",
      deviceId: "mail1",
      guide: {
        title: "An incredible email",
        body: "An email says Marco has won a free sailing trip. Open it and decide: Send reply to accept, or report it.",
      },
      completeOn: { action: "reply", arg: "e_lure1" },
      safeChoice: { label: "Don't reply: report it as phishing", eventId: "reported_lure" },
    },
    {
      id: "b_teach_reply_safe",
      type: "teach",
      title: "No reply, no conversation",
      teach: {
        outcome: "safe",
        body:
          "Marco didn't enter a competition, so he didn't win one. By reporting the email instead of replying, he gave the attacker nothing: no confirmed address, no interest, no conversation to build on. Security can now warn colleagues who got the same message.",
        remember: "If you didn't enter, you didn't win. Don't reply.",
        continueLabel: "See what would have happened",
        whatIf: "If Marco had replied, the attacker would now have a conversation to build on.",
      },
    },
    {
      id: "b_teach_reply",
      type: "teach",
      whatIfNote:
        "If Marco had replied, the attacker would know his address is real and that he wants the prize, and would send a follow-up with a link.",
      title: "A reply is a foothold",
      teach: {
        body:
          "The first email had **no link and no attachment** \u2014 so it sailed past filters and Marco's guard. But by replying, Marco confirmed his address is live, that he's interested, and that the lure works. The attacker now has a warm, two-way conversation \u2014 the perfect setup for the dangerous second email.",
        spot: [
          "'You've won' / 'you've been selected' out of the blue is the oldest hook there is.",
          "Reply-only lures dodge link/attachment scanners \u2014 the bait is the conversation itself.",
          "First-class trips 'for your whole family, nothing to pay' is too good to be true.",
        ],
        protect: [
          "Don't engage with unexpected prizes \u2014 don't reply, don't click.",
          "Verify with the real business directly using contact details you look up yourself.",
          "Report it; if it's targeting you it may be targeting colleagues too.",
        ],
        remember: "If you didn't enter, you didn't win. Don't reply.",
      },
    },
    {
      id: "b_lure2",
      type: "device",
      deviceId: "mail2",
      guide: {
        title: "The follow-up",
        body: "A second email has the trip details and a link: View your prize & claim form. Decide whether you open it.",
      },
      completeOn: { action: "click_link", arg: "e_lure2" },
      safeChoice: { label: "Don't open the link: report the email", eventId: "avoided_lure_link" },
    },
    {
      id: "b_teach_link_safe",
      type: "teach",
      title: "The link stays closed",
      teach: {
        outcome: "safe",
        body:
          "A prize link from a sender you never dealt with is the delivery step of the attack. Leaving it closed and reporting the email means the next page never loads.",
        remember: "Unexpected link, unexpected prize: don't click, report.",
        continueLabel: "See what would have happened",
        whatIf: "If Marco had opened the link, this page would have loaded.",
      },
    },
    {
      id: "b_landing",
      type: "device",
      deviceId: "landing",
      guide: {
        title: "The claim page",
        body: "The owner congratulates Marco on video and offers the prize form to download. Decide what you do.",
      },
      completeOn: { action: "download" },
      safeChoice: { label: "Close the page without downloading anything", eventId: "avoided_download" },
    },
    {
      id: "b_teach_download_safe",
      type: "teach",
      title: "Nothing downloaded",
      teach: {
        outcome: "safe",
        body:
          "A familiar face on video is no longer proof: video can be faked. Closing the page kept the claim form, and whatever is inside it, off Marco's computer.",
        remember: "A friendly face on screen is not proof of who sent it.",
        continueLabel: "See what would have happened",
        whatIf: "If Marco had downloaded the form, this is what it would ask him to do.",
      },
    },
    {
      id: "b_macro",
      type: "device",
      deviceId: "macro",
      guide: {
        title: "Open the form",
        body: "The form says it is locked until you enable content. Enable Content unlocks it. Decide what you do.",
      },
      completeOn: { action: "continue" },
      safeChoice: { label: "Close the document and report it", eventId: "refused_macro" },
    },
    {
      id: "b_teach_macro_safe",
      type: "teach",
      title: "The macro never ran",
      teach: {
        outcome: "safe",
        body:
          "A document that has to be 'unlocked' with Enable Content is asking for permission to run code. Closing it and reporting it means the attacker's code never started.",
        remember: "Don't enable macros to 'unlock' a file \u2014 that's the attack.",
        continueLabel: "See what would have happened",
        whatIf: "If Marco had clicked Enable Content, this is what would have happened.",
      },
    },
    {
      // Safe path only: replays the document so the learner SEES the macro run.
      id: "b_macro_replay",
      type: "device",
      deviceId: "macro",
      forced: true,
      guide: {
        title: "What the macro would do",
        body: "Here is the same document. Click Enable Content to see what happens when the macro runs. Then Autofill my details, Submit claim, and What did that macro really do?",
        actionLabels: ["Enable Content", "Autofill my details", "Submit claim", "What did that macro really do?"],
      },
      whatIfNote: "If Marco had clicked Enable Content, this is what would have happened.",
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_macro",
      type: "teach",
      whatIfNote:
        "If Marco had clicked Enable Content, the macro would have installed a hidden implant that reports back to the attacker, while he typed his family's details into the form.",
      title: "'Enable Content' = run the malware",
      teach: {
        body:
          "That congratulations video was a **deepfake** of the owner, and the claim form was a weaponised macro document. The 'Enable Content' button didn't unlock a form \u2014 it ran the attacker's code. It installed an **in-memory implant** that leaves almost no trace, quietly beaconing back. Marco filled in his family's details while the attacker was already inside.",
        spot: [
          "A document that *requires* you to 'Enable Content' / 'Enable Macros' is a major red flag.",
          "Video and voice can be faked \u2014 seeing a familiar face is no longer proof.",
          "Macro malware can run entirely in memory, hiding from basic antivirus.",
        ],
        protect: [
          "Never enable macros in a document you weren't expecting from a verified source.",
          "Treat unexpected .docm / .xlsm files as hostile.",
          "When in doubt, open documents in protected view and report them.",
        ],
        remember: "Don't enable macros to 'unlock' a file \u2014 that's the attack.",
      },
    },

    { id: "b_title_wfh", type: "title", title: "The next morning \u2014 working from home", autoAdvanceMs: 2800, endsWhatIf: true, guide: { title: "WFH", body: "Marco logs into the company portal from home." } },
    {
      id: "b_wfh",
      type: "device",
      deviceId: "vpn",
      guide: {
        title: "Log in to the portal",
        body: "This is the real company portal, portal.veltora.com, not a fake site. Still the browser warns the connection isn't private: the macro malware on Marco's PC is intercepting it. Decide: Back to safety, or Proceed to portal.veltora.com (unsafe) and sign in.",
        actionLabels: ["Back to safety", "Proceed to portal.veltora.com (unsafe)"],
      },
      completeOn: { action: "compromise" },
      safeChoice: { label: "Back to safety: don't sign in on this connection", eventId: "heeded_cert_warning" },
    },
    {
      id: "b_teach_wfh_safe",
      type: "teach",
      title: "The warning did its job",
      teach: {
        outcome: "safe",
        body:
          "The company portal should never show a certificate warning. Going back and reporting it means Marco's password and his logged-in session never passed through the attacker's connection.",
        remember: "A certificate warning means STOP \u2014 never 'proceed anyway'.",
        continueLabel: "See what would have happened",
        whatIf: "If Marco had clicked through the warning, the attacker would have taken his session.",
      },
    },
    {
      // Safe path only: replays the warning so the learner SEES the hijack.
      id: "b_wfh_replay",
      type: "device",
      deviceId: "vpn",
      forced: true,
      guide: {
        title: "What the attacker would take",
        body: "Here is the same warning. Click Proceed to portal.veltora.com (unsafe) and sign in, to see what the attacker does with Marco's session.",
        actionLabel: "Proceed to portal.veltora.com (unsafe)",
      },
      whatIfNote: "If Marco had clicked through the warning, this is what would have happened.",
      completeOn: { action: "compromise" },
    },
    {
      id: "b_teach_wfh",
      type: "teach",
      whatIfNote:
        "If Marco had clicked through, the attacker would have captured his login and his live session, logged him out with a fake network error, and stepped into Veltora as Marco.",
      title: "The certificate warning was the alarm",
      teach: {
        body:
          "That 'connection is not private' warning was the last line of defence \u2014 and Marco clicked straight through it. The attacker was sitting on the connection: they captured Marco's login, then **hijacked the live session**, kicked him off with a fake 'network failure', and stepped into the company network as Marco.",
        spot: [
          "A certificate / 'not private' warning on a site that should be trusted is a serious red flag.",
          "'Proceed anyway' can hand an attacker your password AND your logged-in session.",
          "A sudden 'temporary network error' right after login can mask a session takeover.",
        ],
        protect: [
          "Never bypass certificate warnings \u2014 stop and report instead.",
          "Access corporate systems only through approved, verified connections/VPN.",
          "Report odd disconnects after login; your session may have been stolen.",
        ],
        remember: "A certificate warning means STOP \u2014 never 'proceed anyway'.",
      },
    },

    { id: "b_hacker_cut2", type: "camera", waypointId: "wp_hacker_establish", environmentId: "hacker" },
    {
      id: "b_title_hacker2",
      type: "title",
      kicker: "Attacker view",
      title: "Back on the attacker's laptop",
      autoAdvanceMs: 9000,
      guide: {
        title: "The attacker",
        body: "The macro implant and Marco's stolen session give the attacker Marco's access to Veltora. You see what they do with it.",
      },
    },
    { id: "b_hacker_push2", type: "camera", waypointId: "wp_hacker_laptop" },
    {
      id: "b_exfil",
      type: "device",
      deviceId: "exfil",
      forced: true,
      guide: {
        title: "Inside the network",
        body: "The attacker is logged in as Marco: the macro implant and the session stolen at the fake certificate warning give him Marco's finance access. He copies the file shares and plants new lure files for colleagues. Click Where does the data go?",
        actionLabel: "Where does the data go?",
      },
      whatIfNote: "If Marco had clicked through the warning, this is what the attacker would do inside the network.",
      completeOn: { action: "continue" },
    },
    {
      id: "b_sale",
      type: "device",
      deviceId: "sale",
      forced: true,
      guide: {
        title: "The payday",
        body: "Why does the attacker have this data? The macro on Marco's PC let him in, and the session he stole at the fake certificate warning opened Veltora's file shares. Now he sells the copy on a darknet market. Click See the damage.",
        actionLabel: "See the damage",
      },
      whatIfNote: "If Marco had clicked through the warning, this is where Veltora's data would end up.",
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_impact",
      type: "teach",
      whatIfNote: "This is the damage the whole chain would have caused if Marco had not stopped it.",
      title: "One overshare, a stolen company",
      teach: {
        body:
          "Look how far one chain went: a public review and a holiday photo gave the attacker everything to craft a flawless lure. A reply, a download, one 'Enable Content', and a bypassed warning later, the whole of Veltora's finance and HR data is for sale \u2014 and infected lure files are spreading to the next victims inside. Every single step had an easy off-ramp.",
        spot: [
          "Targeted attacks are a CHAIN \u2014 breaking any one link stops the whole thing.",
          "The damage isn't just data loss: it's regulatory fines, fraud against staff, and lateral spread.",
          "Stolen data is sold and re-sold \u2014 the harm continues long after the breach.",
        ],
        protect: [
          "Slow down on anything unexpected \u2014 verify on a channel you choose.",
          "Never reply to prizes, never enable macros, never bypass security warnings.",
          "Report early: the sooner a foothold is caught, the less is stolen and spread.",
        ],
        remember: "Break one link in the chain and the attack fails.",
      },
    },

    {
      id: "b_film_title",
      type: "title",
      title: "The next day, back at Veltora",
      endsWhatIf: true,
      autoAdvanceMs: 5000,
      guide: { title: "The next day", body: "Security sends everyone a short film about what we share online." },
    },
    { id: "b_film_office", type: "camera", waypointId: "wp_office_establish", environmentId: "office" },
    { id: "b_film_screen", type: "camera", waypointId: "wp_screen" },
    {
      id: "b_desktop_video",
      type: "device",
      deviceId: "desktop",
      title: "Back at your desk",
      guide: {
        title: "Back at your desk",
        body: "Click Browser. The page explains oversharing and has a short film.",
        actionLabel: "Browser",
      },
      completeOn: { action: "open_app", arg: "browser" },
    },
    {
      id: "b_overshare_video",
      type: "device",
      deviceId: "oversharebrief",
      title: "Watch the film",
      guide: {
        title: "Watch the film",
        body: "Click Watch the video.",
        actionLabel: "Watch the video",
      },
      completeOn: { action: "video_done" },
    },

    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q1",
          title: "Oversharing",
          prompt: "Why does posting a restaurant review and holiday photos publicly increase your risk?",
          hint: "Think about what an attacker can do with it.",
          options: [
            { id: "a", label: "It doesn't \u2014 public posts can't be used against you", icon: "x" },
            { id: "b", label: "Combined, the details let an attacker profile and impersonate you for a tailored lure", correct: true, icon: "shield" },
            { id: "c", label: "Only your password matters; posts are harmless", icon: "lock" },
          ],
          rationale:
            "Attackers research targets from public sources. A review, a photo and a profile combine into a precise picture they use to craft a convincing, personalised spear-phish.",
        },
        {
          id: "q2",
          title: "Too good to be true",
          prompt: "An email says you've won a free first-class holiday and only asks you to REPLY to accept. What should you do?",
          hint: "Did you enter anything?",
          options: [
            { id: "a", label: "Reply to accept \u2014 there's no link, so it's safe", icon: "mail" },
            { id: "b", label: "Don't reply; verify with the real business using details you look up yourself", correct: true, icon: "phone" },
            { id: "c", label: "Reply asking them to prove it's real", icon: "alert" },
          ],
          rationale:
            "Reply-only lures dodge scanners and confirm you're a live, interested target. If you didn't enter, you didn't win \u2014 verify independently instead of engaging.",
        },
        {
          id: "q3",
          title: "Macro documents",
          prompt: "A document won't show its content until you click 'Enable Content'. This is\u2026",
          hint: "What does enabling content actually do?",
          options: [
            { id: "a", label: "Normal \u2014 just unlocking the form", icon: "file" },
            { id: "b", label: "A major red flag \u2014 'Enable Content' runs macros, which can install malware", correct: true, icon: "alert" },
            { id: "c", label: "Safe as long as antivirus is installed", icon: "shield" },
          ],
          rationale:
            "'Enable Content' runs the document's macros. A file that pressures you to enable macros to 'see' it is almost always weaponised \u2014 and in-memory implants can evade basic antivirus.",
        },
        {
          id: "q4",
          title: "Certificate warnings",
          prompt: "Your browser shows 'Your connection is not private' on the company portal. You should\u2026",
          hint: "It's a last line of defence.",
          options: [
            { id: "a", label: "Click 'Proceed anyway' \u2014 you need to work", icon: "power" },
            { id: "b", label: "Stop, don't proceed, and report it \u2014 the warning may mean an attacker is intercepting you", correct: true, icon: "shield" },
            { id: "c", label: "Proceed, but change your password afterwards", icon: "lock" },
          ],
          rationale:
            "A certificate warning on a site you trust can mean someone is intercepting the connection. Proceeding can hand over your password and your live session \u2014 stop and report instead.",
        },
        {
          id: "q5",
          title: "Stopping the chain",
          prompt: "A targeted attack is a chain of steps. What's the best way to defend against it?",
          hint: "You only have to break one link.",
          options: [
            { id: "a", label: "Nothing works \u2014 if you're targeted, you're doomed", icon: "x" },
            { id: "b", label: "Break any one link: verify surprises, don't reply/enable/proceed, and report early", correct: true, icon: "check" },
            { id: "c", label: "Only the IT department can stop it", icon: "alert" },
          ],
          rationale:
            "Every step in the chain had an off-ramp. Verifying the prize, not replying, not enabling macros, or heeding the certificate warning would each have stopped the entire attack. Reporting early limits the damage.",
        },
      ],
    },

    { id: "b_debrief", type: "debrief", title: "Debrief" },
  ],

  edges: [
    { from: "b_intro", to: "b_rest_establish" },
    { from: "b_rest_establish", to: "b_rest_zoom" },
    { from: "b_rest_zoom", to: "b_review" },
    { from: "b_review", to: "b_title_vacation" },
    { from: "b_title_vacation", to: "b_vac_cut" },
    { from: "b_vac_cut", to: "b_insta" },
    { from: "b_insta", to: "b_teach_recon_safe", on: "safe" },
    { from: "b_teach_recon_safe", to: "b_teach_recon" },
    { from: "b_insta", to: "b_teach_recon" },
    { from: "b_teach_recon", to: "b_hacker_cut" },
    { from: "b_hacker_cut", to: "b_title_hacker" },
    { from: "b_title_hacker", to: "b_hacker_push" },
    { from: "b_hacker_push", to: "b_osint" },
    { from: "b_osint", to: "b_teach_osint" },
    { from: "b_teach_osint", to: "b_title_office" },
    { from: "b_title_office", to: "b_office_cut" },
    { from: "b_office_cut", to: "b_to_desk" },
    { from: "b_to_desk", to: "b_to_screen" },
    { from: "b_to_screen", to: "b_lure1" },
    { from: "b_lure1", to: "b_teach_reply_safe", on: "safe" },
    { from: "b_teach_reply_safe", to: "b_teach_reply" },
    { from: "b_lure1", to: "b_teach_reply" },
    { from: "b_teach_reply", to: "b_lure2" },
    { from: "b_lure2", to: "b_teach_link_safe", on: "safe" },
    { from: "b_teach_link_safe", to: "b_landing" },
    { from: "b_lure2", to: "b_landing" },
    { from: "b_landing", to: "b_teach_download_safe", on: "safe" },
    { from: "b_teach_download_safe", to: "b_macro" },
    { from: "b_landing", to: "b_macro" },
    { from: "b_macro", to: "b_teach_macro_safe", on: "safe" },
    { from: "b_teach_macro_safe", to: "b_macro_replay" },
    { from: "b_macro_replay", to: "b_teach_macro" },
    { from: "b_macro", to: "b_teach_macro" },
    { from: "b_teach_macro", to: "b_title_wfh" },
    { from: "b_title_wfh", to: "b_wfh" },
    { from: "b_wfh", to: "b_teach_wfh_safe", on: "safe" },
    { from: "b_teach_wfh_safe", to: "b_wfh_replay" },
    { from: "b_wfh_replay", to: "b_teach_wfh" },
    { from: "b_wfh", to: "b_teach_wfh" },
    { from: "b_teach_wfh", to: "b_hacker_cut2" },
    { from: "b_hacker_cut2", to: "b_title_hacker2" },
    { from: "b_title_hacker2", to: "b_hacker_push2" },
    { from: "b_hacker_push2", to: "b_exfil" },
    { from: "b_exfil", to: "b_sale" },
    { from: "b_sale", to: "b_teach_impact" },
    { from: "b_teach_impact", to: "b_film_title" },
    { from: "b_film_title", to: "b_film_office" },
    { from: "b_film_office", to: "b_film_screen" },
    { from: "b_film_screen", to: "b_desktop_video" },
    { from: "b_desktop_video", to: "b_overshare_video" },
    { from: "b_overshare_video", to: "b_quiz" },
    { from: "b_quiz", to: "b_debrief" },
  ],

  initialBeatId: "b_intro",
  passThreshold: 70,
};
